import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Award, Gauge as GaugeIcon, Globe2 } from "lucide-react";
import { PageShell, Eyebrow } from "../components/Layout";
import { FRAMEWORKS } from "../data/mock";

const CATS = ["All", ...Array.from(new Set(FRAMEWORKS.map((f) => f.category)))];

export const Frameworks = () => {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const list = FRAMEWORKS.filter((f) => (cat === "All" || f.category === cat) && (f.name + f.full + f.blurb).toLowerCase().includes(q.toLowerCase()));

  return (
    <PageShell>
      <section className="hero-grid border-b border-neutral-200">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 pt-16 pb-12">
          <Eyebrow>The framework atlas</Eyebrow>
          <h1 className="font-heading text-5xl lg:text-[60px] font-bold tracking-tight text-ink leading-[1.02]">Framework Library</h1>
          <p className="mt-5 text-[16px] text-ink/60 max-w-2xl leading-relaxed">36+ risk frameworks indexed — global standards and Australian prudential instruments, each with complexity, certification and time-to-implement at a glance.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search frameworks…" data-testid="frameworks-search-input"
              className="border border-neutral-300 rounded-md px-4 py-2.5 text-sm w-full sm:w-80 outline-none focus:ring-2 focus:ring-accent bg-white" />
            <div className="flex flex-wrap gap-2">
              {CATS.map((c) => (
                <button key={c} onClick={() => setCat(c)} data-testid={`framework-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`font-mono text-[11.5px] px-3 py-1.5 rounded-sm border transition-colors duration-150 ${cat === c ? "bg-ink text-white border-ink" : "border-neutral-300 text-ink/60 hover:border-ink"}`}>{c}</button>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <p className="font-mono text-[11px] text-neutral-400 tracking-wider mb-6">{list.length} FRAMEWORKS</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((f) => (
              <Link key={f.slug} to={`/frameworks/${f.slug}`} data-testid={`framework-card-${f.slug}`}
                className="group border border-neutral-200 rounded-lg p-6 hover:border-accent/40 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(15,27,45,0.08)] transition-[border-color,transform,box-shadow] duration-200 bg-white">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent">{f.category}</span>
                  <span className="font-mono text-[10px] text-neutral-400">{f.origin}</span>
                </div>
                <h3 className="font-heading text-xl font-bold text-ink mt-3">{f.name}</h3>
                <p className="text-[12.5px] text-ink/45 mt-0.5">{f.full}</p>
                <p className="text-[13px] text-ink/60 leading-relaxed mt-3 line-clamp-3">{f.blurb}</p>
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-neutral-100">
                  <span className="font-mono text-[11px] text-neutral-400">Complexity · {f.complexity}</span>
                  <span className="inline-flex items-center gap-1 text-accent text-[12.5px] font-semibold group-hover:gap-2 transition-[gap] duration-200">Open <ArrowRight size={13} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export const FrameworkDetail = () => {
  const { slug } = useParams();
  const f = FRAMEWORKS.find((x) => x.slug === slug) || FRAMEWORKS[0];
  const related = FRAMEWORKS.filter((x) => x.category === f.category && x.slug !== f.slug).slice(0, 3);

  const facts = [
    { icon: GaugeIcon, label: "Complexity", value: f.complexity },
    { icon: Award, label: "Certification", value: f.certification },
    { icon: Clock, label: "Time to implement", value: f.time },
    { icon: Globe2, label: "Origin", value: f.origin },
  ];

  return (
    <PageShell>
      <section className="hero-grid border-b border-neutral-200">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 pt-14 pb-12">
          <Link to="/frameworks" className="inline-flex items-center gap-1.5 font-mono text-[11.5px] text-neutral-400 hover:text-accent transition-colors duration-150 mb-8" data-testid="back-to-library">
            <ArrowLeft size={13} /> FRAMEWORK LIBRARY
          </Link>
          <span className="block font-mono text-[11px] tracking-[0.25em] uppercase text-accent mb-3">{f.category}</span>
          <h1 className="font-heading text-5xl lg:text-[56px] font-bold tracking-tight text-ink leading-[1.02]">{f.name}</h1>
          <p className="mt-3 text-[18px] text-ink/50">{f.full}</p>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-10">
            <div>
              <h2 className="font-heading text-2xl font-bold text-ink mb-4">Overview</h2>
              <p className="text-[15px] text-ink/70 leading-[1.8]">{f.blurb}</p>
            </div>
            <div className="border-l-[3px] border-accent bg-paper rounded-r-lg p-6">
              <h3 className="font-mono text-[11px] tracking-[0.25em] uppercase text-accent mb-3">Australian context</h3>
              <p className="text-[15px] text-ink/70 leading-[1.8]">{f.au}</p>
            </div>
            {related.length > 0 && (
              <div>
                <h2 className="font-heading text-2xl font-bold text-ink mb-5">Related in {f.category}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {related.map((r) => (
                    <Link key={r.slug} to={`/frameworks/${r.slug}`} className="border border-neutral-200 rounded-lg p-5 hover:border-accent/40 transition-colors duration-200">
                      <h4 className="font-heading font-bold text-ink">{r.name}</h4>
                      <p className="text-[12px] text-ink/50 mt-1">{r.full}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <aside className="lg:col-span-4">
            <div className="border border-neutral-200 rounded-lg overflow-hidden sticky top-24">
              <div className="bg-ink text-white px-5 py-3.5 font-mono text-[11px] tracking-[0.25em] uppercase">At a glance</div>
              {facts.map((fact) => (
                <div key={fact.label} className="flex items-start gap-3 px-5 py-4 border-b border-neutral-100 last:border-0">
                  <fact.icon size={16} className="text-accent mt-0.5 shrink-0" />
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400">{fact.label}</p>
                    <p className="text-[13.5px] text-ink font-medium mt-0.5">{fact.value}</p>
                  </div>
                </div>
              ))}
              <div className="p-5">
                <Link to="/tools/cars-calculator" className="block text-center bg-accent text-white rounded-md py-2.5 text-[13px] font-semibold hover:bg-accent-dark transition-colors duration-200">Match me to a framework</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </PageShell>
  );
};
